import type { Difficulty } from './ChartDocumentV1.js';

/**
 * `manifest.json` of a Song Pack v1 archive.
 *
 * Mirrors Unity's `SongPackFormat.Manifest`. The Python engine writes a dictionary
 * with the identical key set and identical fixed file names
 * (`infrastructure/songpack.py`), so one reader serves both producers.
 */

/** Version 1 fixes the payload file names exactly. */
export const MUSIC_FILE = 'music.wav';
export const COVER_FILE = 'cover.png';
export const MANIFEST_FILE = 'manifest.json';

/**
 * Extension key naming this song's music video.
 *
 * Version 1 already reserves `extensions` as free-form string key/value pairs,
 * which is why carrying a music video needs **no format change at all**: the
 * key is written into a field every existing reader already parses and every
 * existing writer already round-trips.
 *
 * The value is read in two different ways depending on what is in the archive:
 *
 *   - it names an entry that *is* in the pack -> the video travels with the song
 *   - it names anything else -> an external reference, resolved as before
 *
 * That split is deliberate. It means one key covers both, and it means a pack
 * whose video entry went missing (Unity's rebuild export path drops unknown
 * entries while preserving `extensions`) degrades to the external lookup rather
 * than to a broken song.
 */
export const MV_FILE_EXTENSION_KEY = 'mvFile';

/** The entry name this runtime writes a bundled video to. */
export const DEFAULT_VIDEO_FILE = 'video.mp4';

export interface ChartReferenceV1 {
  readonly difficulty: string;
  readonly file: string;
}

export interface ExtensionPropertyV1 {
  readonly key: string;
  readonly value: string;
}

export interface ManifestV1 {
  readonly formatVersion: number;
  /** Stable package identifier. Never displayed. */
  readonly packId: string;
  /** Stable song identifier. Never displayed. */
  readonly songId: string;
  /** Canonical proper name. UI must show this verbatim. */
  readonly displayName: string;
  readonly bpm: number;
  readonly endGraceSeconds: number;
  readonly dspLeadTime: number;
  readonly musicFile: string;
  /** Empty string when the pack carries no cover. */
  readonly coverFile: string;
  readonly charts: readonly ChartReferenceV1[];
  readonly author: string;
  readonly description: string;
  readonly tags: readonly string[];
  /**
   * Free-form string key/value pairs. Version 1 already reserves this as the
   * forward-compatible place for per-song overrides, so the runtime can read
   * song-specific tuning without any format change.
   */
  readonly extensions: readonly ExtensionPropertyV1[];
}

/** Defaults for absent keys, taken from Unity's `Manifest` field initialisers. */
export const MANIFEST_DEFAULTS = Object.freeze({
  bpm: 120,
  endGraceSeconds: 3,
  dspLeadTime: 0.2,
  musicFile: MUSIC_FILE,
  coverFile: '',
  author: '',
  description: '',
});

/** Maximum identifier length, from Unity's `ValidateId`. */
export const MAX_ID_LENGTH = 96;

/**
 * Identifier charset, from Unity's `ValidateId`: ASCII letters, digits, `_`, `-`
 * and `.`. The Python engine enforces the same set in `write_song_pack`.
 */
const ID_PATTERN = /^[A-Za-z0-9_.-]+$/;

export function isValidId(value: string): boolean {
  return value.length > 0 && value.length <= MAX_ID_LENGTH && ID_PATTERN.test(value);
}

/** Looks up a chart reference by difficulty, case-insensitively (Unity parity). */
export function findChartReference(
  manifest: ManifestV1,
  difficulty: Difficulty,
): ChartReferenceV1 | undefined {
  return manifest.charts.find(
    (reference) => reference.difficulty.toLowerCase() === difficulty,
  );
}

/** Reads an `extensions` entry by exact key, or `undefined` when absent. */
export function readExtension(manifest: ManifestV1, key: string): string | undefined {
  return manifest.extensions.find((property) => property.key === key)?.value;
}

/** The music video reference this manifest declares, trimmed, or `undefined`. */
export function readMvFileReference(manifest: ManifestV1): string | undefined {
  const value = readExtension(manifest, MV_FILE_EXTENSION_KEY)?.trim();
  return value === undefined || value.length === 0 ? undefined : value;
}

/**
 * Replaces (or removes, with `undefined`) the music video reference.
 *
 * Rebuilds the array rather than mutating it: `extensions` is declared readonly
 * because a manifest is a record of what was read, and a caller that wants a
 * different one is producing a new pack.
 */
export function withMvFileReference(
  manifest: ManifestV1,
  reference: string | undefined,
): ManifestV1 {
  const others = manifest.extensions.filter(
    (property) => property.key !== MV_FILE_EXTENSION_KEY,
  );
  return {
    ...manifest,
    extensions:
      reference === undefined || reference.length === 0
        ? others
        : [...others, { key: MV_FILE_EXTENSION_KEY, value: reference }],
  };
}
